import { DetailsSection } from "@frontend/components/collab/details-sections";
import { formatINR } from "@creators/backend/lib/money";
import type { CollabRoom } from "@creators/backend/lib/collabs/queries";
import {
  summariseDelivery,
  summarisePayer,
  type ReliabilitySummary,
  type ReliabilityTone,
} from "@frontend/lib/reliability";

type Side = "creator" | "brand" | "admin";

const TONE_TEXT: Record<ReliabilityTone, string> = {
  green: "text-money-700",
  amber: "text-warn-700",
  red: "text-red-700",
  neutral: "text-ink-subtle",
};

/**
 * The other party's track record on the one thing you are trusting them with:
 * paying, if you are delivering, and delivering, if you are paying.
 *
 * It is deliberately about the counterparty only — a scorecard of your own
 * record in your own sidebar would be noise at best, and something to game at
 * worst. Admins see both because they are reading the room rather than in it.
 */
export function TrustCard({ room, viewer }: { room: CollabRoom; viewer: Side }) {
  const payerName = room.brand.companyName || room.brand.name || room.brand.phone;

  const entries: { title: string; subject: string; summary: ReliabilitySummary }[] = [];

  if (viewer === "creator" || viewer === "admin") {
    entries.push({
      title: "Payment record",
      subject: payerName,
      summary: summarisePayer(room.trust.payer),
    });
  }

  if (viewer === "brand" || viewer === "admin") {
    entries.push({
      title: "Delivery record",
      subject: room.creator.displayName,
      summary: summariseDelivery(room.trust.delivery),
    });
  }

  if (entries.length === 0) return null;

  const outstanding = room.trust.payer.outstanding;
  const showOutstanding = viewer !== "brand" && outstanding > 0;

  return (
    <DetailsSection title="Track record" compact>
      <ul className="divide-y divide-line/60">
        {entries.map((entry) => (
          <li key={entry.title} className="px-3 py-2">
            <div className="flex items-baseline justify-between gap-3">
              <p className="truncate text-[11px] font-medium tracking-wide text-ink-subtle uppercase">
                {entry.title}
              </p>
              {entry.summary.value ? (
                <p
                  className={`tabular shrink-0 text-sm font-semibold ${TONE_TEXT[entry.summary.tone]}`}
                >
                  {entry.summary.value}
                </p>
              ) : (
                <p className="shrink-0 text-[11px] font-medium text-ink-subtle">Not enough yet</p>
              )}
            </div>
            <p className="mt-0.5 text-[11px] leading-snug text-ink-muted">{entry.summary.detail}</p>
          </li>
        ))}
      </ul>

      {showOutstanding ? (
        <p className="border-t border-line/60 bg-canvas/60 px-3 py-1.5 text-[11px] leading-snug text-ink-subtle">
          {formatINR(outstanding)} of theirs is unconfirmed across all collabs.
        </p>
      ) : null}
    </DetailsSection>
  );
}
